import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { BarcodeLabelModal } from '../components/BarcodeLabel'
import FbPostModal from '../components/FbPostModal'
import { ProductCardModal } from '../components/ProductCard'
import { getCurrencySymbol } from '../utils/currencies'

const CATEGORIES = [
  'Clothing','Sarees','Shoes & Sneakers','Leather Items & Bags','Watches & Accessories',
  'Sunglasses','Jewellery','Gold & Precious','Perfumes','Home Decor','Furniture',
  'Kitchen & Cookware','Bedding & Linen','Electronics & Appliances','Mobile & Gadgets',
  'Computer & Accessories','Food & Catering','Organic Foods','Herbal Products',
  'Grocery Items','Bakery & Sweets','Beverages','Medicines','Supplements & Vitamins',
  'Medical Equipment','Beauty & Skincare','Cosmetics & Manufacturing','Hair Care',
  'Toys & Games','Kids Clothing','Baby Products','Pet Food & Supplies','Pet Accessories',
  'Plants, Trees & Seeds','Flowers & Gift Items','Books','Stationeries','Online Courses',
  'Online Subscriptions & Gift Cards','Sports & Fitness','Arts & Crafts',
  'Construction Materials','Vehicles','Land & Property','Other',
]

const ADJUST_REASONS = [
  'Damaged / defective','Lost / missing','Stock count correction',
  'Returned to supplier','Sample / display','Other',
]

const OFFER_TYPES = [
  { value:'percent',       label:'% Discount' },
  { value:'fixed',         label:'Fixed amount off' },
  { value:'bundle',        label:'Bundle deal' },
  { value:'free_delivery', label:'Free delivery' },
  { value:'custom',        label:'Custom offer' },
]

function Icon({ path, size=14, color='var(--text-muted)', strokeWidth=1.8, fill='none' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill}
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
      <path d={path}/>
    </svg>
  )
}

const IC = {
  edit:    'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  trash:   'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  back:    'M19 12H5M12 5l-7 7 7 7',
  restock: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  adjust:  'M12 20V4M5 13l7-7 7 7',
  img:     'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  check:   'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  x:       'M6 18L18 6M6 6l12 12',
  copy:    'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  orders:  'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  warn:    'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  fb:      'M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z',
  tag:     'M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01',
  ext:     'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3',
  plus:    'M12 5v14M5 12h14',
  photo:   'M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z M12 17a4 4 0 100-8 4 4 0 000 8z',
  calendar:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
}

const inp = {
  width:'100%', border:'1px solid var(--border)', borderRadius:'9px',
  padding:'9px 12px', fontSize:'12.5px', outline:'none',
  background:'var(--bg-card)', color:'var(--text-primary)',
  fontFamily:'Space Grotesk, sans-serif', boxSizing:'border-box',
  transition:'border-color 0.15s',
}

function SectionCard({ title, color, icon, children }) {
  const c = color || 'var(--primary)'
  return (
    <div style={{ background:'var(--bg-card)', borderRadius:'12px', border:'1px solid var(--border)', overflow:'hidden', marginBottom:'14px' }}>
      <div style={{ padding:'10px 16px', borderBottom:'1px solid var(--border)', borderLeft:'3px solid '+c, background:'var(--bg-muted)' }}>
        <div style={{ fontSize:'11px', fontWeight:700, color:c, textTransform:'uppercase', letterSpacing:'0.07em', display:'flex', alignItems:'center', gap:'6px' }}>
          {icon && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.5" strokeLinecap="round"><path d={icon}/></svg>}
          {title}
        </div>
      </div>
      <div style={{ padding:'16px' }}>{children}</div>
    </div>
  )
}

function LightboxCanvas({ PW, PH, scale, offsetX, offsetY, HEADER, FOOTER, IMG_H, drawCanvas, onClose }) {
  const ref = useRef()
  useEffect(() => {
    const node = ref.current; if (!node) return
    const maxW = Math.min(window.innerWidth - 40, 600)
    const maxH = window.innerHeight - 80
    let w = maxW; let h = Math.round(maxW * PH / PW)
    if (h > maxH) { h = maxH; w = Math.round(maxH * PW / PH) }
    node.width = PW; node.height = PH
    node.style.width = w + 'px'; node.style.height = h + 'px'; node.style.borderRadius = '12px'
    drawCanvas(node, PW, PH, scale, offsetX, offsetY, HEADER, FOOTER, IMG_H)
  }, [PW, PH, scale, offsetX, offsetY, HEADER, FOOTER, IMG_H, drawCanvas])
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'20px' }}>
      <button onClick={onClose} style={{ position:'absolute', top:'16px', right:'16px', width:'38px', height:'38px', borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
      </button>
      <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.5)', position:'absolute', bottom:'20px' }}>Click anywhere to close</div>
      <canvas ref={ref} onClick={e=>e.stopPropagation()} style={{ display:'block', boxShadow:'0 20px 60px rgba(0,0,0,0.5)' }}/>
    </div>
  )
}

function CropModal({ src, onSave, onCancel, shopName='', productCode='', logoUrl='' }) {
  const canvasRef  = useRef(); const imgRef = useRef(); const logoImgRef = useRef()
  const [scale, setScale]         = useState(1)
  const [offsetX, setOffsetX]     = useState(0)
  const [offsetY, setOffsetY]     = useState(0)
  const [dragging, setDragging]   = useState(false)
  const [startPos, setStartPos]   = useState({ x:0, y:0 })
  const [startOff, setStartOff]   = useState({ x:0, y:0 })
  const [showWatermark, setShowWatermark] = useState(false)
  const [showCode, setShowCode]   = useState(true)
  const [showLogo, setShowLogo]   = useState(false)
  const [codeText, setCodeText]   = useState(productCode)
  const [imgReady, setImgReady]   = useState(false)
  const [logoReady, setLogoReady] = useState(false)
  const [lightbox, setLightbox]   = useState(false)
  const PW = 280; const OUT_W = 1080
  const HEADER = Math.round(PW * 0.05); const FOOTER = Math.round(PW * 0.04); const TEAL_PX = 1
  const [naturalRatio, setNaturalRatio] = useState(1)
  const IMG_H = Math.round(PW * naturalRatio); const PH = HEADER + TEAL_PX + IMG_H + TEAL_PX + FOOTER

  useEffect(() => { setCodeText(productCode) }, [productCode])

  const handleImgLoad = useCallback(() => {
    const img = imgRef.current; if (!img) return
    const ratio = img.naturalHeight / img.naturalWidth
    setNaturalRatio(ratio)
    const fitScale = PW / img.naturalWidth
    setScale(fitScale); setOffsetX(0); setOffsetY(0); setImgReady(true)
  }, [PW])

  const drawCanvas = useCallback((canvas, W, H, scl, offX, offY, header, footer, imgH) => {
    const img = imgRef.current; if (!canvas || !img) return
    const ctx = canvas.getContext('2d')
    const NAVY = '#0B1F3A'; const TEAL = '#2DD4B0'
    const TEAL_LINE = Math.max(2, Math.round(W * 0.004))
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H)
    ctx.fillStyle = NAVY; ctx.fillRect(0, 0, W, header)
    const logoMaxH = header - 14; let logoEndX = 14
    if (showLogo && logoUrl && logoReady && logoImgRef.current) {
      const logo = logoImgRef.current; const logoAR = logo.naturalWidth / logo.naturalHeight
      const logoH = Math.min(logoMaxH, header - 14); const logoW = logoH * logoAR
      logoEndX = 14 + logoW + 8; ctx.drawImage(logo, 14, (header - logoH) / 2, logoW, logoH)
    }
    if (shopName) { ctx.fillStyle='#fff'; ctx.font=`${Math.round(header*0.38)}px Arial`; ctx.textAlign='left'; ctx.textBaseline='middle'; ctx.fillText(shopName, logoEndX, header/2) }
    ctx.fillStyle = TEAL; ctx.fillRect(0, header, W, TEAL_LINE)
    const imgAreaY = header + TEAL_LINE
    const sw = img.naturalWidth * scl; const sh = img.naturalHeight * scl
    const ix = (W - sw) / 2 + offX; const iy = imgAreaY + (imgH - sh) / 2 + offY
    ctx.save(); ctx.beginPath(); ctx.rect(0, imgAreaY, W, imgH); ctx.clip()
    ctx.fillStyle = '#f8f8f8'; ctx.fillRect(0, imgAreaY, W, imgH); ctx.drawImage(img, ix, iy, sw, sh)
    if (showWatermark && shopName) {
      ctx.globalAlpha = 0.15; ctx.fillStyle = '#fff'; ctx.font = `bold ${Math.round(W*0.05)}px Arial`
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.translate(W/2, imgAreaY+imgH/2); ctx.rotate(-Math.PI/6)
      const step = W * 0.4
      for (let dy = -H; dy < H; dy += step) ctx.fillText('© '+shopName, 0, dy)
    }
    ctx.restore()
    ctx.fillStyle = TEAL; ctx.fillRect(0, imgAreaY + imgH, W, TEAL_LINE)
    const footerY = imgAreaY + imgH + TEAL_LINE
    ctx.fillStyle = NAVY; ctx.fillRect(0, footerY, W, footer)
    if (showCode && codeText) {
      ctx.fillStyle='#fff'; ctx.font=`bold ${Math.round(footer*0.38)}px "Courier New", monospace`
      ctx.textAlign='left'; ctx.textBaseline='middle'; ctx.fillText(codeText, 14, footerY+footer/2)
    }
    ctx.globalAlpha=0.6; ctx.fillStyle=TEAL; ctx.font=`${Math.round(footer*0.28)}px Arial`
    ctx.textAlign='right'; ctx.textBaseline='middle'
    ctx.fillText('Powered by pageconnect.io', W-14, footerY+footer/2); ctx.globalAlpha=1
    ctx.strokeStyle='rgba(255,255,255,0.18)'; ctx.lineWidth=0.5; ctx.beginPath()
    ctx.moveTo(W/3,imgAreaY);ctx.lineTo(W/3,imgAreaY+imgH);ctx.moveTo(W*2/3,imgAreaY);ctx.lineTo(W*2/3,imgAreaY+imgH)
    ctx.moveTo(0,imgAreaY+imgH/3);ctx.lineTo(W,imgAreaY+imgH/3);ctx.moveTo(0,imgAreaY+imgH*2/3);ctx.lineTo(W,imgAreaY+imgH*2/3)
    ctx.stroke()
  }, [showWatermark, showCode, showLogo, shopName, codeText, logoReady, logoUrl]) // eslint-disable-line

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas || !imgReady) return
    canvas.width = PW; canvas.height = PH
    drawCanvas(canvas, PW, PH, scale, offsetX, offsetY, HEADER, FOOTER, IMG_H)
  }, [drawCanvas, imgReady, PW, PH, HEADER, FOOTER, IMG_H, scale, offsetX, offsetY]) // eslint-disable-line

  const handleTouchStart = (e) => { if (e.touches.length===1) { setDragging(true); setStartPos({x:e.touches[0].clientX,y:e.touches[0].clientY}); setStartOff({x:offsetX,y:offsetY}) } }
  const handleTouchMove  = (e) => { e.preventDefault(); if (e.touches.length===1 && dragging) { setOffsetX(startOff.x+e.touches[0].clientX-startPos.x); setOffsetY(startOff.y+e.touches[0].clientY-startPos.y) } }

  const handleSave = useCallback(() => {
    const OW=OUT_W; const ratio=OW/PW
    const oHeader=Math.round(HEADER*ratio); const oFooter=Math.round(FOOTER*ratio)
    const oTeal=2; const oImgH=Math.round(IMG_H*ratio); const OH=oHeader+oTeal+oImgH+oTeal+oFooter
    const outCanvas=document.createElement('canvas'); outCanvas.width=OW; outCanvas.height=OH
    drawCanvas(outCanvas,OW,OH,scale*ratio,offsetX*ratio,offsetY*ratio,oHeader,oFooter,oImgH)
    outCanvas.toBlob(blob=>{ if(blob) onSave(blob) },'image/jpeg',0.92)
  }, [scale, offsetX, offsetY, drawCanvas, PW, HEADER, FOOTER, IMG_H, onSave])

  const CheckRow = ({ checked, onChange, label, sub, children }) => (
    <div style={{ marginBottom:'10px' }}>
      <button onClick={()=>onChange(!checked)} style={{ width:'100%', display:'flex', alignItems:'flex-start', gap:'10px', padding:'9px 12px', borderRadius:'9px', border:'1px solid', borderColor:checked?'var(--primary)':'var(--border)', background:checked?'var(--primary-light)':'var(--bg-card)', cursor:'pointer', textAlign:'left' }}>
        <div style={{ width:'16px', height:'16px', borderRadius:'4px', border:'2px solid', borderColor:checked?'var(--primary)':'var(--border)', background:checked?'var(--primary)':'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, marginTop:'1px' }}>
          {checked && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
        </div>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:'12.5px', fontWeight:600, color:checked?'var(--primary)':'var(--text-primary)' }}>{label}</div>
          {sub && <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>{sub}</div>}
        </div>
      </button>
      {checked && children && <div style={{ marginTop:'6px', paddingLeft:'4px' }}>{children}</div>}
    </div>
  )

  return (
    <>
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'16px' }}>
        <div style={{ background:'var(--bg-card)', borderRadius:'16px', padding:'20px', width:'320px', fontFamily:'Space Grotesk', maxHeight:'96vh', overflow:'auto' }}>
          <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px' }}>Crop & compose photo</div>
          <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'12px' }}>Drag to reposition · Zoom to fit</div>
          {logoUrl && <img ref={logoImgRef} src={logoUrl} alt="" onLoad={()=>setLogoReady(true)} style={{ display:'none' }} crossOrigin="anonymous"/>}
          <img ref={imgRef} src={src} alt="crop" onLoad={handleImgLoad} style={{ display:'none' }}/>
          <div style={{ position:'relative', marginBottom:'12px' }}>
            <canvas ref={canvasRef}
              style={{ borderRadius:'10px', border:'1px solid var(--border)', display:'block', margin:'0 auto', touchAction:'none', width:'100%', height:'auto', cursor:dragging?'grabbing':'grab' }}
              onMouseDown={e=>{ setDragging(true); setStartPos({x:e.clientX,y:e.clientY}); setStartOff({x:offsetX,y:offsetY}) }}
              onMouseMove={e=>{ if(!dragging) return; setOffsetX(startOff.x+e.clientX-startPos.x); setOffsetY(startOff.y+e.clientY-startPos.y) }}
              onMouseUp={()=>setDragging(false)} onMouseLeave={()=>setDragging(false)}
              onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={()=>setDragging(false)}/>
            <button onClick={()=>setLightbox(true)} style={{ position:'absolute', bottom:'8px', right:'8px', padding:'5px 10px', borderRadius:'7px', background:'rgba(11,31,58,0.75)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:'5px' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round"><path d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"/></svg>
              <span style={{ fontSize:'11px', color:'#fff', fontWeight:600 }}>Full preview</span>
            </button>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
            <span style={{ fontSize:'11px', color:'var(--text-muted)', flexShrink:0 }}>Zoom</span>
            <input type="range" min={0.05} max={4} step={0.02} value={scale} onChange={e=>setScale(Number(e.target.value))} style={{ flex:1, accentColor:'var(--primary)' }}/>
            <span style={{ fontSize:'11px', color:'var(--text-muted)', flexShrink:0 }}>{Math.round(scale*100)}%</span>
          </div>
          <div style={{ borderTop:'1px solid var(--border)', paddingTop:'12px', marginBottom:'12px' }}>
            <div style={{ fontSize:'11.5px', fontWeight:700, color:'var(--text-muted)', marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.06em' }}>Overlays</div>
            <CheckRow checked={showLogo} onChange={setShowLogo} label="Brand logo header" sub={logoUrl?'Your shop logo + name at top':'No logo found — add in Website Builder'}/>
            <CheckRow checked={showCode} onChange={setShowCode} label="Product code footer" sub="Shop name + code at bottom">
              <input value={codeText} onChange={e=>setCodeText(e.target.value)} placeholder="e.g. SAR-RED-761" style={{ ...inp, fontSize:'12px', fontFamily:'monospace', padding:'7px 10px' }}/>
            </CheckRow>
            <CheckRow checked={showWatermark} onChange={setShowWatermark} label="Shop name watermark" sub="Diagonal © overlay on image"/>
          </div>
          <div style={{ display:'flex', gap:'10px' }}>
            <button onClick={onCancel} style={{ flex:1, padding:'10px', borderRadius:'9px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Cancel</button>
            <button onClick={handleSave} style={{ flex:1, padding:'10px', borderRadius:'9px', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:600, color:'#fff', cursor:'pointer' }}>Save photo</button>
          </div>
        </div>
      </div>
      {lightbox && imgReady && (
        <LightboxCanvas PW={PW} PH={PH} scale={scale} offsetX={offsetX} offsetY={offsetY}
          HEADER={HEADER} FOOTER={FOOTER} IMG_H={IMG_H} drawCanvas={drawCanvas} onClose={()=>setLightbox(false)}/>
      )}
    </>
  )
}

function AddPostLinkModal({ productId, sellerId, onSave, onClose }) {
  const [url, setUrl] = useState(''); const [note, setNote] = useState(''); const [saving, setSaving] = useState(false)
  const handleSave = async () => {
    if (!url.trim()) return; setSaving(true)
    await supabase.from('fb_posts').insert({ seller_id:sellerId, product_id:productId, post_url:url.trim(), note:note.trim()||null, status:'published', posted_at:new Date().toISOString() })
    await supabase.from('products').update({ facebook_post_url:url.trim() }).eq('id', productId)
    setSaving(false); onSave()
  }
  return (
    <div onClick={e=>e.target===e.currentTarget&&onClose()} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200, padding:'16px' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:'16px', padding:'24px', maxWidth:'400px', width:'100%', fontFamily:'Space Grotesk' }}>
        <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', marginBottom:'16px' }}>Add Facebook post link</div>
        <div style={{ marginBottom:'12px' }}>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Post URL *</label>
          <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://facebook.com/..." style={inp} autoFocus/>
        </div>
        <div style={{ marginBottom:'20px' }}>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Label <span style={{ fontWeight:400 }}>optional</span></label>
          <input value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. Eid collection launch" style={inp}/>
        </div>
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px', borderRadius:'10px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving||!url.trim()} style={{ flex:2, padding:'11px', borderRadius:'10px', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:!url.trim()?'not-allowed':'pointer', opacity:!url.trim()?0.6:1 }}>
            {saving?'Saving...':'Save post link'}
          </button>
        </div>
      </div>
    </div>
  )
}

function ProductDetail() {
  const { id }     = useParams()
  const navigate   = useNavigate()
  const { seller } = useAuth()
  const currency   = seller?.seller_currency || 'BDT'
  const sym        = getCurrencySymbol(currency)
  const sellerLogo = seller?.logo_url || ''

  const [product, setProduct]                     = useState(null)
  const [variants, setVariants]                   = useState([])
  const [stockLog, setStockLog]                   = useState([])
  const [fbPosts, setFbPosts]                     = useState([])
  const [orderCount, setOrderCount]               = useState(null)
  const [loading, setLoading]                     = useState(true)
  const [editMode, setEditMode]                   = useState(false)
  const [editForm, setEditForm]                   = useState({})
  const [savingEdit, setSavingEdit]               = useState(false)
  const [editSaved, setEditSaved]                 = useState(false)
  const [showRestock, setShowRestock]             = useState(false)
  const [showAdjust, setShowAdjust]               = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showBarcodeModal, setShowBarcodeModal]   = useState(false)
  const [showCardModal, setShowCardModal]         = useState(false)
  const [showFbPost, setShowFbPost]               = useState(false)
  const [showAddPostLink, setShowAddPostLink]     = useState(false)
  const [restockQty, setRestockQty]               = useState('')
  const [restockVariantId, setRestockVariantId]   = useState(null)
  const [restockPrice, setRestockPrice]           = useState('')
  const [restockNote, setRestockNote]             = useState('')
  const [adjustQty, setAdjustQty]                 = useState('')
  const [adjustReason, setAdjustReason]           = useState(ADJUST_REASONS[0])
  const [adjustNote, setAdjustNote]               = useState('')
  const [saving, setSaving]                       = useState(false)
  const [deleting, setDeleting]                   = useState(false)
  const [copiedPostId, setCopiedPostId]           = useState(null)
  const [togglingOffer, setTogglingOffer]         = useState(false)
  const [postPhotoLightbox, setPostPhotoLightbox] = useState(false)
  const [imagePreview, setImagePreview]           = useState(null)
  const [imageFile, setImageFile]                 = useState(null)
  const [cropSrc, setCropSrc]                     = useState(null)
  const [cropMode, setCropMode]                   = useState('thumbnail')
  const [uploadingImg, setUploadingImg]           = useState(false)
  const [postPhotoPreview, setPostPhotoPreview]   = useState(null)
  const [postPhotoFile, setPostPhotoFile]         = useState(null)
  const imgInputRef     = useRef()
  const postImgInputRef = useRef()

  const setEF = (key, val) => setEditForm(f=>({...f,[key]:val}))

  const fetchProduct = async () => {
    setLoading(true)
    const [{ data:prod }, { data:vars }, { data:logs }, { count }, { data:posts }] = await Promise.all([
      supabase.from('products').select('*').eq('id',id).single(),
      supabase.from('product_variants').select('*').eq('product_id',id).order('created_at',{ascending:true}),
      supabase.from('stock_log').select('*').eq('product_id',id).order('created_at',{ascending:false}).limit(15),
      supabase.from('orders').select('*',{count:'exact',head:true}).eq('product_id',id),
      supabase.from('fb_posts').select('*').eq('product_id',id).order('posted_at',{ascending:false}).limit(5),
    ])
    if (prod) {
      setProduct(prod)
      setImagePreview(prod.image_url ? prod.image_url+'?t='+Date.now() : null)
      setPostPhotoPreview(prod.post_photo_url||null)
      setEditForm({
        name:prod.name||'', product_type:prod.product_type||'',
        category:prod.category||'', color:prod.color||'',
        sell_price:prod.sell_price||'', buy_price:prod.buy_price||'',
        vat_percent:prod.vat_percent||'',
        season:prod.season||'all_year', is_pinned:prod.is_pinned||false,
        notes:prod.notes||'', additional_details:prod.additional_details||'',
        facebook_post_url:prod.facebook_post_url||'', status:prod.status||'active',
        offer_active:prod.offer_active||false,
        offer_type:prod.offer_type||'percent',
        offer_value:prod.offer_value||'',
        offer_label:prod.offer_label||'',
        offer_valid_from:prod.offer_valid_from||'',
        offer_valid_until:prod.offer_valid_until||'',
        // ── NEW fields ──
        variable_pricing:prod.variable_pricing||false,
        best_price:prod.best_price||'',
        advance_payment_type:prod.advance_payment_type||'none',
        advance_payment_value:prod.advance_payment_value||'',
        posted_on_facebook:prod.posted_on_facebook||false,
        fb_posted_date:prod.fb_posted_date||'',
      })
    }
    setVariants(vars||[]); setStockLog(logs||[]); setOrderCount(count||0); setFbPosts(posts||[])
    setLoading(false)
  }

  useEffect(() => { fetchProduct() }, [id]) // eslint-disable-line

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = ev => { setCropSrc(ev.target.result); setCropMode('thumbnail') }
    reader.readAsDataURL(file); e.target.value=''
  }
  const handlePostImageSelect = (e) => {
    const file = e.target.files?.[0]; if (!file) return
    const reader = new FileReader()
    reader.onload = ev => { setCropSrc(ev.target.result); setCropMode('post') }
    reader.readAsDataURL(file); e.target.value=''
  }
  const handleCropSave = (blob) => {
    if (cropMode==='thumbnail') { setImageFile(blob); setImagePreview(URL.createObjectURL(blob)) }
    else { setPostPhotoFile(blob); setPostPhotoPreview(URL.createObjectURL(blob)) }
    setCropSrc(null)
  }
  const uploadImage = async () => {
    if (!imageFile) return null
    try {
      setUploadingImg(true)
      const path = seller.id+'/'+id+'.jpg'
      const { error } = await supabase.storage.from('product-images').upload(path, imageFile, { upsert:true, contentType:'image/jpeg' })
      setUploadingImg(false); if (error) return null
      const { data } = supabase.storage.from('product-images').getPublicUrl(path)
      return data?.publicUrl||null
    } catch { setUploadingImg(false); return null }
  }
  const uploadPostPhoto = async () => {
    if (!postPhotoFile) return null
    try {
      const path = seller.id+'/'+id+'-post.jpg'
      const { error } = await supabase.storage.from('product-images').upload(path, postPhotoFile, { upsert:true, contentType:'image/jpeg' })
      if (error) return null
      const { data } = supabase.storage.from('product-images').getPublicUrl(path)
      return data?.publicUrl||null
    } catch { return null }
  }

  const handleSaveEdit = async () => {
    setSavingEdit(true)
    const [imageUrl, postPhotoUrl] = await Promise.all([uploadImage(), uploadPostPhoto()])
    const updateData = {
      name:editForm.name, product_type:editForm.product_type,
      category:editForm.category, color:editForm.color,
      sell_price:editForm.sell_price||null, buy_price:editForm.buy_price||null,
      vat_percent:editForm.vat_percent||null,
      season:editForm.season, is_pinned:editForm.is_pinned,
      notes:editForm.notes, additional_details:editForm.additional_details,
      facebook_post_url:editForm.facebook_post_url, status:editForm.status,
      offer_active:editForm.offer_active,
      offer_type:editForm.offer_active?editForm.offer_type:null,
      offer_value:editForm.offer_active&&editForm.offer_value?Number(editForm.offer_value):null,
      offer_label:editForm.offer_active?editForm.offer_label||null:null,
      offer_valid_from:editForm.offer_active&&editForm.offer_valid_from?editForm.offer_valid_from:null,
      offer_valid_until:editForm.offer_active&&editForm.offer_valid_until?editForm.offer_valid_until:null,
      // ── NEW fields ──
      variable_pricing:editForm.variable_pricing,
      best_price:editForm.variable_pricing&&editForm.best_price?Number(editForm.best_price):null,
      advance_payment_type:editForm.advance_payment_type||'none',
      advance_payment_value:editForm.advance_payment_type!=='none'&&editForm.advance_payment_type!=='full'&&editForm.advance_payment_value?Number(editForm.advance_payment_value):null,
      posted_on_facebook:editForm.posted_on_facebook,
      fb_posted_date:editForm.posted_on_facebook&&editForm.fb_posted_date?editForm.fb_posted_date:null,
    }
    if (imageUrl)     { updateData.image_url=imageUrl; setImagePreview(imageUrl+'?t='+Date.now()) }
    if (postPhotoUrl) { updateData.post_photo_url=postPhotoUrl; setPostPhotoPreview(postPhotoUrl) }
    await supabase.from('products').update(updateData).eq('id',id)
    if (editForm.offer_active !== product?.offer_active || editForm.offer_value !== product?.offer_value) {
      const offerDesc = editForm.offer_active
        ? `Offer activated: ${editForm.offer_type==='percent'?editForm.offer_value+'% off':editForm.offer_type==='fixed'?sym+editForm.offer_value+' off':editForm.offer_label||'Special offer'}${editForm.offer_label?' — '+editForm.offer_label:''}${editForm.offer_valid_until?' until '+editForm.offer_valid_until:''}`
        : 'Offer deactivated'
      await supabase.from('activity_logs').insert({ entity_type:'product', entity_id:id, action:'offer_changed', done_by:'Owner', seller_id:seller?.id, extra_data:{ description:offerDesc, name:editForm.name } })
    }
    await supabase.from('activity_logs').insert({ entity_type:'product', entity_id:id, action:'product_edited', done_by:'Owner', seller_id:seller?.id, extra_data:{ name:editForm.name, sell_price:editForm.sell_price } })
    await fetchProduct()
    setSavingEdit(false); setEditSaved(true); setEditMode(false)
    setImageFile(null); setPostPhotoFile(null)
    setTimeout(()=>setEditSaved(false),3000)
  }

  const handleToggleOffer = async () => {
    setTogglingOffer(true)
    const newActive = !product.offer_active
    await supabase.from('products').update({ offer_active:newActive }).eq('id',id)
    await supabase.from('activity_logs').insert({ entity_type:'product', entity_id:id, action:'offer_changed', done_by:'Owner', seller_id:seller?.id, extra_data:{ description:newActive?'Offer re-activated':'Offer deactivated', name:product.name } })
    await fetchProduct(); setTogglingOffer(false)
  }

  const handleDeleteProduct = async () => {
    setDeleting(true)
    await supabase.from('activity_logs').insert({ entity_type:'product', entity_id:id, action:'product_deleted', done_by:'Owner', seller_id:seller?.id, extra_data:{ name:product.name, product_code:product.product_code, stock:product.stock } })
    await supabase.from('stock_log').delete().eq('product_id',id)
    await supabase.from('product_variants').delete().eq('product_id',id)
    await supabase.from('products').delete().eq('id',id)
    setDeleting(false); navigate('/inventory')
  }

  const handleRestock = async () => {
    if (!restockQty) return; setSaving(true)
    const qty = Number(restockQty)
    if (restockVariantId) {
      const variant = variants.find(v=>v.id===restockVariantId)
      if (variant) {
        await supabase.from('product_variants').update({ stock:(variant.stock||0)+qty }).eq('id',restockVariantId)
        const { data:allVariants } = await supabase.from('product_variants').select('stock').eq('product_id',id)
        const totalStock = allVariants?.reduce((sum,v)=>sum+(v.stock||0),0)||0
        await supabase.from('products').update({ stock:totalStock }).eq('id',id)
      }
    } else {
      await supabase.from('products').update({ stock:(product.stock||0)+qty }).eq('id',id)
    }
    await supabase.from('stock_log').insert({ product_id:id, action:'restock', quantity:qty, buy_price:restockPrice||null, note:restockNote, done_by:'Owner' })
    await fetchProduct()
    setShowRestock(false); setRestockQty(''); setRestockPrice(''); setRestockNote(''); setRestockVariantId(null); setSaving(false)
  }

  const handleAdjust = async () => {
    if (!adjustQty||Number(adjustQty)<=0) return; setSaving(true)
    const qty = Number(adjustQty); const newStock = Math.max(0,(product.stock||0)-qty)
    await supabase.from('products').update({ stock:newStock }).eq('id',id)
    await supabase.from('stock_log').insert({ product_id:id, action:'adjustment', quantity:-qty, note:adjustReason+(adjustNote?' — '+adjustNote:''), done_by:'Owner' })
    await fetchProduct()
    setShowAdjust(false); setAdjustQty(''); setAdjustNote(''); setAdjustReason(ADJUST_REASONS[0]); setSaving(false)
  }

  const handleDeletePost = async (postId) => { await supabase.from('fb_posts').delete().eq('id',postId); await fetchProduct() }
  const handleCopyPostUrl = (url, postId) => { navigator.clipboard.writeText(url); setCopiedPostId(postId); setTimeout(()=>setCopiedPostId(null),2000) }

  const fmt        = (n) => sym+Number(n||0).toLocaleString()
  const stockColor = (s) => s===0?'var(--error)':s<=5?'var(--warning)':'var(--success)'
  const stockBg    = (s) => s===0?'#FEF2F2':s<=5?'#FFF8E6':'#F0FDF4'
  const stockLabel = (s) => s===0?'Out of stock':s<=5?'Low stock':'In stock'

  const offerSummary = (p) => {
    if (!p?.offer_active) return null
    const val = p.offer_value
    if (p.offer_type==='percent')       return `${val}% off${p.offer_label?' — '+p.offer_label:''}`
    if (p.offer_type==='fixed')         return `${sym}${val} off${p.offer_label?' — '+p.offer_label:''}`
    if (p.offer_type==='bundle')        return p.offer_label||'Bundle deal'
    if (p.offer_type==='free_delivery') return 'Free delivery'+(p.offer_label?' — '+p.offer_label:'')
    return p.offer_label||'Special offer'
  }
  const isOfferExpired = (p) => p?.offer_valid_until && new Date(p.offer_valid_until) < new Date()

  if (loading) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-page)', fontFamily:'Space Grotesk' }}>
      <style>{`@keyframes pd-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid var(--primary)', borderTopColor:'transparent', animation:'pd-spin 0.7s linear infinite', margin:'0 auto 10px' }}/>
        <div style={{ fontSize:'13px', color:'var(--text-muted)' }}>Loading product...</div>
      </div>
    </div>
  )
  if (!product) return (
    <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-page)' }}>
      <div style={{ fontSize:'13px', color:'var(--text-muted)' }}>Product not found</div>
    </div>
  )

  const profit      = product.sell_price&&product.buy_price ? Number(product.sell_price)-Number(product.buy_price) : null
  const margin      = profit&&product.sell_price ? Math.round((profit/Number(product.sell_price))*100) : null
  const vatAmount   = product.vat_percent&&product.sell_price ? Math.round(Number(product.sell_price)*Number(product.vat_percent)/100) : null
  const hasVariants = variants.length > 0
  const offer       = offerSummary(product)
  const offerExpired = isOfferExpired(product)

  return (
    <div style={{ flex:1, background:'var(--bg-page)', overflowX:'hidden', fontFamily:'Space Grotesk, sans-serif' }}>
      <Topbar title="Product Detail"/>
      <div style={{ padding:'18px 24px', paddingBottom:'80px' }}>

        {/* ── Stock banners ── */}
        {product.stock===0 && (
          <div style={{ background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'12px', padding:'12px 16px', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <Icon path={IC.warn} color="#DC2626" size={16} strokeWidth={2}/>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'#DC2626' }}>Out of stock</div>
                <div style={{ fontSize:'11.5px', color:'#B91C1C' }}>This product won't appear as available</div>
              </div>
            </div>
            {!hasVariants && <button onClick={()=>{ setRestockVariantId(null); setShowRestock(true) }} style={{ padding:'6px 12px', borderRadius:'8px', background:'#DC2626', border:'none', fontSize:'12px', fontWeight:700, color:'#fff', cursor:'pointer', flexShrink:0 }}>Restock now</button>}
          </div>
        )}
        {product.stock>0 && product.stock<=5 && (
          <div style={{ background:'#FFF8E6', border:'1px solid #FDE68A', borderRadius:'12px', padding:'12px 16px', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <Icon path={IC.warn} color="#D97706" size={16} strokeWidth={2}/>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'#D97706' }}>Low stock — only {product.stock} left</div>
                <div style={{ fontSize:'11.5px', color:'#92400E' }}>Consider restocking soon</div>
              </div>
            </div>
            {!hasVariants && <button onClick={()=>{ setRestockVariantId(null); setShowRestock(true) }} style={{ padding:'6px 12px', borderRadius:'8px', background:'#D97706', border:'none', fontSize:'12px', fontWeight:700, color:'#fff', cursor:'pointer', flexShrink:0 }}>Restock</button>}
          </div>
        )}
        {offerExpired && (
          <div style={{ background:'#FFF8E6', border:'1px solid #FDE68A', borderRadius:'12px', padding:'12px 16px', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <Icon path={IC.tag} color="#D97706" size={16} strokeWidth={2}/>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'#D97706' }}>Offer expired</div>
                <div style={{ fontSize:'11.5px', color:'#92400E' }}>Ended {product.offer_valid_until} — update or turn it off</div>
              </div>
            </div>
            <button onClick={()=>setEditMode(true)} style={{ padding:'6px 12px', borderRadius:'8px', background:'#D97706', border:'none', fontSize:'12px', fontWeight:700, color:'#fff', cursor:'pointer', flexShrink:0 }}>Update offer</button>
          </div>
        )}

        {/* ── Header card ── */}
        <div style={{ background:'var(--bg-card)', borderRadius:'12px', border:'1px solid var(--border)', padding:'16px', marginBottom:'14px' }}>
          <div className="pd-header-layout" style={{ display:'flex', gap:'14px', alignItems:'flex-start' }}>

            {/* LEFT */}
            <div style={{ flex:1, minWidth:0 }}>

              {/* Thumbnail + name */}
              <div style={{ display:'flex', alignItems:'flex-start', gap:'12px', marginBottom:'12px' }}>
                <div style={{ flexShrink:0 }}>
                  {imagePreview
                    ? <img src={imagePreview} alt={product.name} style={{ width:'80px', height:'80px', borderRadius:'12px', objectFit:'cover', border:'1px solid var(--border)' }}/>
                    : <div style={{ width:'80px', height:'80px', borderRadius:'12px', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', border:'1px solid var(--border)' }}>
                        <Icon path={IC.img} size={28} strokeWidth={1.4}/>
                      </div>}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  {product.product_type && <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'2px' }}>{product.product_type}</div>}
                  <div style={{ fontSize:'17px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px', lineHeight:1.3 }}>{product.name}</div>
                  {product.color && <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'5px' }}>{product.color}</div>}
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'5px' }}>
                    <span style={{ fontSize:'11px', fontWeight:700, color:'var(--primary)', background:'var(--primary-light)', padding:'2px 8px', borderRadius:'6px', fontFamily:'JetBrains Mono, monospace' }}>{product.product_code}</span>
                    {product.category && <span style={{ fontSize:'11px', color:'var(--text-muted)', background:'var(--bg-muted)', padding:'2px 8px', borderRadius:'20px' }}>{product.category}</span>}
                    <span style={{ fontSize:'11px', fontWeight:600, padding:'2px 8px', borderRadius:'20px', background:stockBg(product.stock), color:stockColor(product.stock) }}>{stockLabel(product.stock)}</span>
                    {product.is_pinned && <span style={{ fontSize:'11px', background:'#FFF8E6', color:'#D97706', padding:'2px 8px', borderRadius:'20px' }}>⭐ Pinned</span>}
                    {offer && !offerExpired && <span style={{ fontSize:'11px', background:'rgba(45,212,176,0.1)', color:'var(--success)', padding:'2px 8px', borderRadius:'20px', fontWeight:600 }}>🏷 {offer}</span>}
                  </div>
                </div>
                <button onClick={()=>setEditMode(!editMode)} style={{ padding:'6px 12px', borderRadius:'8px', border:'1px solid', borderColor:editMode?'var(--border)':'var(--primary)', background:editMode?'var(--bg-muted)':'var(--primary-light)', fontSize:'12px', fontWeight:600, color:editMode?'var(--text-muted)':'var(--primary)', cursor:'pointer', flexShrink:0 }}>
                  {editMode?'Cancel':'Edit'}
                </button>
              </div>

              {/* 4-stat grid */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'6px', marginBottom:'8px' }} className="pd-stats">
                {[
                  { label:'Stock',        value:product.stock,           color:stockColor(product.stock) },
                  { label:'Sell price',   value:fmt(product.sell_price), color:'var(--text-primary)' },
                  { label:'Buy price 🔒', value:fmt(product.buy_price),  color:'var(--text-muted)' },
                  { label:'Total orders', value:orderCount??'—',         color:'var(--primary)' },
                ].map(s=>(
                  <div key={s.label} style={{ background:'var(--bg-muted)', borderRadius:'9px', padding:'8px', textAlign:'center' }}>
                    <div style={{ fontSize:'16px', fontWeight:700, color:s.color }}>{s.value}</div>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)', marginTop:'2px' }}>{s.label}</div>
                  </div>
                ))}
              </div>

              {/* ── Product info row — created date, FB status, variable pricing, advance ── */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:'10px', marginBottom:'10px', padding:'8px 10px', background:'var(--bg-muted)', borderRadius:'9px', alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11.5px', color:'var(--text-muted)' }}>
                  <Icon path={IC.calendar} size={12} color="var(--text-muted)" strokeWidth={2}/>
                  Added: <strong style={{ color:'var(--text-primary)' }}>
                    {new Date(product.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'numeric'})}
                  </strong>
                </div>
                {product.posted_on_facebook ? (
                  <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11.5px', color:'#1877F2' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    FB posted{product.fb_posted_date
                      ? ' · '+new Date(product.fb_posted_date).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'numeric'})
                      : ''}
                    <button onClick={()=>setEditMode(true)} style={{ marginLeft:'4px', fontSize:'10.5px', color:'#1877F2', background:'rgba(24,119,242,0.1)', border:'none', borderRadius:'4px', padding:'1px 6px', cursor:'pointer', fontFamily:'Space Grotesk, sans-serif' }}>Change date</button>
                  </div>
                ) : (
                  <div style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'11.5px', color:'var(--text-muted)' }}>
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
                    Not yet posted on Facebook
                    <button onClick={()=>setEditMode(true)} style={{ marginLeft:'4px', fontSize:'10.5px', color:'var(--primary)', background:'var(--primary-light)', border:'none', borderRadius:'4px', padding:'1px 6px', cursor:'pointer', fontFamily:'Space Grotesk, sans-serif' }}>Mark as posted</button>
                  </div>
                )}
                {product.variable_pricing && product.best_price && (
                  <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11.5px', color:'#7C3AED', background:'#F5F3FF', padding:'2px 8px', borderRadius:'6px' }}>
                    🔀 Bargaining on · floor {fmt(product.best_price)}
                  </div>
                )}
                {product.advance_payment_type && product.advance_payment_type !== 'none' && (
                  <div style={{ display:'flex', alignItems:'center', gap:'4px', fontSize:'11.5px', color:'#0D9488', background:'rgba(13,148,136,0.08)', padding:'2px 8px', borderRadius:'6px' }}>
                    💳 {product.advance_payment_type==='full'
                      ? 'Full payment required'
                      : product.advance_payment_type==='percent'
                        ? product.advance_payment_value+'% advance'
                        : fmt(product.advance_payment_value)+' advance'}
                  </div>
                )}
              </div>

              {(profit!==null||vatAmount!==null) && (
                <div style={{ display:'grid', gridTemplateColumns:profit!==null&&vatAmount!==null?'1fr 1fr':'1fr', gap:'6px', marginBottom:'10px' }}>
                  {profit!==null && (
                    <div style={{ background:'#F0FDF4', borderRadius:'9px', padding:'8px 12px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:'11.5px', color:'var(--success)' }}>Profit / unit</span>
                      <span style={{ fontSize:'12px', fontWeight:700, color:'var(--success)' }}>{fmt(profit)} <span style={{ fontSize:'10px', fontWeight:400 }}>({margin}%)</span></span>
                    </div>
                  )}
                  {vatAmount!==null && (
                    <div style={{ background:'#F5F3FF', borderRadius:'9px', padding:'8px 12px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                      <span style={{ fontSize:'11.5px', color:'#7C3AED' }}>VAT {product.vat_percent}%</span>
                      <span style={{ fontSize:'12px', fontWeight:700, color:'#7C3AED' }}>{fmt(vatAmount)}</span>
                    </div>
                  )}
                </div>
              )}

              {editSaved && (
                <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:'9px', padding:'8px 12px', marginBottom:'10px', display:'flex', alignItems:'center', gap:'8px' }}>
                  <Icon path={IC.check} color="var(--success)" size={14} strokeWidth={2}/>
                  <span style={{ fontSize:'12.5px', color:'var(--success)', fontWeight:600 }}>Product updated successfully</span>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                <button onClick={()=>navigate('/inventory')} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 12px', borderRadius:'8px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer' }}>
                  <Icon path={IC.back} color="var(--text-primary)" size={12} strokeWidth={2}/>Back
                </button>
                {!hasVariants && (
                  <>
                    <button onClick={()=>{ setRestockVariantId(null); setShowRestock(true) }} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 12px', borderRadius:'8px', background:'var(--success)', border:'none', fontSize:'12px', fontWeight:600, color:'#fff', cursor:'pointer' }}>
                      <Icon path={IC.restock} color="#fff" size={12} strokeWidth={2}/>Restock
                    </button>
                    <button onClick={()=>setShowAdjust(true)} style={{ display:'flex', alignItems:'center', gap:'5px', padding:'7px 12px', borderRadius:'8px', background:'var(--bg-muted)', border:'1px solid var(--border)', fontSize:'12px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer' }}>
                      <Icon path={IC.adjust} color="var(--text-primary)" size={12} strokeWidth={2}/>Adjust
                    </button>
                  </>
                )}
                <button onClick={()=>setShowBarcodeModal(true)} style={{ padding:'7px 12px', borderRadius:'8px', background:'var(--bg-muted)', border:'1px solid var(--border)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Barcode</button>
                <button onClick={()=>setShowCardModal(true)} style={{ padding:'7px 12px', borderRadius:'8px', background:'var(--bg-muted)', border:'1px solid var(--border)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Card</button>
              </div>
            </div>

            {/* RIGHT — post photo */}
            <div style={{ flexShrink:0, width:'150px' }} className="pd-post-photo-col">
              <div style={{ fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'6px' }}>Post photo</div>
              {postPhotoPreview
                ? <div style={{ position:'relative', marginBottom:'6px', cursor:'pointer' }} onClick={()=>setPostPhotoLightbox(true)}>
                    <img src={postPhotoPreview} alt={product.name} style={{ width:'150px', borderRadius:'10px', display:'block' }}/>
                  </div>
                : <div onClick={()=>postImgInputRef.current?.click()} style={{ width:'150px', height:'200px', borderRadius:'10px', background:'var(--bg-muted)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', cursor:'pointer', border:'1.5px dashed var(--border)', gap:'6px', marginBottom:'6px' }}>
                    <Icon path={IC.photo} size={26} color="var(--text-muted)" strokeWidth={1.2}/>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)', textAlign:'center', padding:'0 10px' }}>Upload post photo</div>
                  </div>
              }
              <button onClick={()=>postImgInputRef.current?.click()} style={{ width:'100%', padding:'6px', borderRadius:'7px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'Space Grotesk, sans-serif', marginBottom:'4px' }}>
                {postPhotoPreview?'Change photo':'Upload photo'}
              </button>
              <button onClick={()=>setShowFbPost(true)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px', padding:'7px', borderRadius:'7px', background:'#1877F2', border:'none', fontSize:'11.5px', fontWeight:600, color:'#fff', cursor:'pointer', fontFamily:'Space Grotesk, sans-serif' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Post to Facebook
              </button>
            </div>
          </div>
        </div>

        {/* ── Edit form ── */}
        {editMode && (
          <>
            <div className="pd-edit-inline">
              <SectionCard title="Edit product" color="var(--primary)" icon={IC.edit}>
                <EditFormContent editForm={editForm} setEF={setEF}
                  imagePreview={imagePreview} setImagePreview={setImagePreview} setImageFile={setImageFile}
                  imgInputRef={imgInputRef} handleImageSelect={handleImageSelect}
                  postPhotoPreview={postPhotoPreview} setPostPhotoPreview={setPostPhotoPreview}
                  setPostPhotoFile={setPostPhotoFile} postImgInputRef={postImgInputRef}
                  handlePostImageSelect={handlePostImageSelect}
                  savingEdit={savingEdit} uploadingImg={uploadingImg}
                  handleSaveEdit={handleSaveEdit} setShowDeleteConfirm={setShowDeleteConfirm}
                  sym={sym} IC={IC} inp={inp}/>
              </SectionCard>
            </div>
            <div className="pd-edit-sheet" style={{ display:'none', position:'fixed', inset:0, zIndex:100 }}>
              <div onClick={()=>setEditMode(false)} style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.5)' }}/>
              <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'var(--bg-card)', borderRadius:'20px 20px 0 0', maxHeight:'90vh', display:'flex', flexDirection:'column' }}>
                <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                  <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)' }}>Edit product</div>
                  <button onClick={()=>setEditMode(false)} style={{ background:'none', border:'none', cursor:'pointer' }}>
                    <Icon path={IC.x} size={18} color="var(--text-muted)" strokeWidth={2}/>
                  </button>
                </div>
                <div style={{ overflowY:'auto', flex:1, padding:'16px 20px 32px' }}>
                  <EditFormContent editForm={editForm} setEF={setEF}
                    imagePreview={imagePreview} setImagePreview={setImagePreview} setImageFile={setImageFile}
                    imgInputRef={imgInputRef} handleImageSelect={handleImageSelect}
                    postPhotoPreview={postPhotoPreview} setPostPhotoPreview={setPostPhotoPreview}
                    setPostPhotoFile={setPostPhotoFile} postImgInputRef={postImgInputRef}
                    handlePostImageSelect={handlePostImageSelect}
                    savingEdit={savingEdit} uploadingImg={uploadingImg}
                    handleSaveEdit={handleSaveEdit} setShowDeleteConfirm={setShowDeleteConfirm}
                    sym={sym} IC={IC} inp={inp}/>
                </div>
              </div>
            </div>
          </>
        )}
        <input ref={imgInputRef}     type="file" accept="image/*" style={{ display:'none' }} onChange={handleImageSelect}/>
        <input ref={postImgInputRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handlePostImageSelect}/>

        {/* ── Active offer card ── */}
        {(product.offer_active || product.offer_type) && (
          <div style={{ background:product.offer_active&&!offerExpired?'rgba(45,212,176,0.05)':'var(--bg-card)', border:'1px solid', borderColor:product.offer_active&&!offerExpired?'rgba(45,212,176,0.25)':'var(--border)', borderRadius:'12px', padding:'16px', marginBottom:'14px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:product.offer_active?'12px':'0' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={product.offer_active&&!offerExpired?'var(--success)':'var(--text-muted)'} strokeWidth="2" strokeLinecap="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82zM7 7h.01"/></svg>
                <div style={{ fontSize:'13px', fontWeight:700, color:product.offer_active&&!offerExpired?'var(--success)':offerExpired?'var(--error)':'var(--text-muted)' }}>
                  {product.offer_active&&!offerExpired?'Active offer':offerExpired?'Expired offer':'Offer (inactive)'}
                </div>
              </div>
              <div style={{ display:'flex', gap:'6px' }}>
                <button onClick={()=>setEditMode(true)} style={{ padding:'5px 10px', borderRadius:'7px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Edit offer</button>
                <button onClick={handleToggleOffer} disabled={togglingOffer} style={{ padding:'5px 10px', borderRadius:'7px', border:'none', background:product.offer_active?'#FEF2F2':'#F0FDF4', fontSize:'11.5px', fontWeight:600, color:product.offer_active?'var(--error)':'var(--success)', cursor:'pointer' }}>
                  {togglingOffer?'...':product.offer_active?'Turn off':'Turn on'}
                </button>
              </div>
            </div>
            {product.offer_active && (
              <div style={{ display:'flex', gap:'16px', flexWrap:'wrap' }}>
                <div><div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'2px' }}>Offer</div><div style={{ fontSize:'14px', fontWeight:700, color:'var(--success)' }}>{offer}</div></div>
                {product.offer_valid_from && <div><div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'2px' }}>Starts</div><div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>{product.offer_valid_from}</div></div>}
                {product.offer_valid_until && <div><div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'2px' }}>Ends</div><div style={{ fontSize:'13px', fontWeight:600, color:offerExpired?'var(--error)':'var(--text-primary)' }}>{product.offer_valid_until}</div></div>}
              </div>
            )}
          </div>
        )}
        {!product.offer_active && !product.offer_type && (
          <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'12px', padding:'12px 16px', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ fontSize:'12.5px', color:'var(--text-muted)' }}>No active offer on this product</div>
            <button onClick={()=>setEditMode(true)} style={{ padding:'5px 12px', borderRadius:'7px', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Add offer</button>
          </div>
        )}

        {/* ── Facebook posts history ── */}
        <SectionCard title="Facebook posts" color="#1877F2" icon={IC.fb}>
          {fbPosts.length===0 ? (
            <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'12px' }}>No posts tracked yet. Post this product on Facebook and add the link here.</div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'12px' }}>
              {fbPosts.map((post,i)=>(
                <div key={post.id} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', background:'var(--bg-muted)', borderRadius:'10px' }}>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', marginBottom:'2px' }}>{post.note||`Post ${fbPosts.length-i}`}</div>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>
                      {post.posted_at ? new Date(post.posted_at).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'numeric'}) : 'Date unknown'}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:'5px', flexShrink:0 }}>
                    {post.post_url && (
                      <>
                        <a href={post.post_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:'4px', padding:'5px 9px', borderRadius:'7px', background:'#EFF6FF', color:'#1877F2', fontSize:'11.5px', fontWeight:600, textDecoration:'none' }}>
                          <Icon path={IC.ext} size={11} color="#1877F2" strokeWidth={2}/>Open
                        </a>
                        <button onClick={()=>handleCopyPostUrl(post.post_url, post.id)} style={{ display:'flex', alignItems:'center', gap:'4px', padding:'5px 9px', borderRadius:'7px', border:'1px solid var(--border)', background:copiedPostId===post.id?'#F0FDF4':'var(--bg-card)', fontSize:'11.5px', fontWeight:600, color:copiedPostId===post.id?'var(--success)':'var(--text-muted)', cursor:'pointer' }}>
                          <Icon path={IC.copy} size={11} color={copiedPostId===post.id?'var(--success)':'var(--text-muted)'} strokeWidth={2}/>
                          {copiedPostId===post.id?'Copied!':'Copy'}
                        </button>
                      </>
                    )}
                    <button onClick={()=>handleDeletePost(post.id)} style={{ padding:'5px 7px', borderRadius:'7px', border:'1px solid #FECACA', background:'#FEF2F2', cursor:'pointer' }}>
                      <Icon path={IC.trash} size={11} color="#DC2626" strokeWidth={2}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {fbPosts.length<5 && (
            <button onClick={()=>setShowAddPostLink(true)} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'9px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>
              <Icon path={IC.plus} size={13} color="var(--text-muted)" strokeWidth={2}/>
              Add post link {fbPosts.length>0?`(${fbPosts.length}/5)`:''}
            </button>
          )}
          {fbPosts.length>=5 && <div style={{ fontSize:'11.5px', color:'var(--text-muted)' }}>Max 5 posts tracked. Delete one to add new.</div>}
        </SectionCard>

        {/* ── Barcode + Card ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'14px' }}>
          <div style={{ background:'var(--bg-card)', borderRadius:'12px', border:'1px solid var(--border)', padding:'14px' }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:'var(--primary)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'8px' }}>Barcode labels</div>
            <div style={{ fontFamily:'JetBrains Mono, monospace', fontSize:'12px', fontWeight:700, color:'var(--primary)', background:'var(--primary-light)', padding:'5px 10px', borderRadius:'7px', marginBottom:'8px', display:'inline-block' }}>{product.product_code}</div>
            <div><button onClick={()=>setShowBarcodeModal(true)} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px', background:'var(--primary)', border:'none', fontSize:'12px', fontWeight:600, color:'#fff', cursor:'pointer' }}>Print labels</button></div>
          </div>
          <div style={{ background:'var(--bg-card)', borderRadius:'12px', border:'1px solid var(--border)', padding:'14px' }}>
            <div style={{ fontSize:'11px', fontWeight:700, color:'var(--success)', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:'8px' }}>Product card</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'8px', lineHeight:1.5 }}>1080×1080px PNG for social media</div>
            <button onClick={()=>setShowCardModal(true)} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'8px', background:'var(--success)', border:'none', fontSize:'12px', fontWeight:600, color:'#fff', cursor:'pointer' }}>Generate card</button>
          </div>
        </div>

        {/* ── Variants ── */}
        {hasVariants && (
          <SectionCard title="Stock by variant" color="#7C3AED" icon="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v11">
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {variants.map(v=>(
                <div key={v.id} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                  <div style={{ width:'54px', padding:'6px 8px', borderRadius:'8px', background:'#F5F3FF', border:'1px solid #DDD6FE', fontSize:'12px', fontWeight:700, color:'#7C3AED', textAlign:'center', flexShrink:0 }}>{v.variant_name}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                      <div style={{ flex:1, height:'4px', background:'var(--border)', borderRadius:'2px', overflow:'hidden' }}>
                        <div style={{ height:'100%', borderRadius:'2px', width:Math.min((v.stock/20)*100,100)+'%', background:stockColor(v.stock) }}/>
                      </div>
                      <span style={{ fontSize:'12px', fontWeight:700, color:stockColor(v.stock), width:'24px', textAlign:'right' }}>{v.stock}</span>
                    </div>
                    <div style={{ fontSize:'11px', color:'var(--text-muted)' }}>{fmt(v.sell_price||product.sell_price||0)}</div>
                  </div>
                  <button onClick={()=>{ setRestockVariantId(v.id); setShowRestock(true) }} style={{ padding:'6px 10px', borderRadius:'8px', background:'#F0FDF4', border:'1px solid #BBF7D0', fontSize:'11.5px', fontWeight:600, color:'var(--success)', cursor:'pointer', flexShrink:0 }}>+ Restock</button>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* ── Product details ── */}
        {(product.additional_details||product.notes) && (
          <SectionCard title="Product details" color="var(--text-muted)" icon="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z">
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {product.additional_details && <div><div style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', marginBottom:'3px' }}>Additional details</div><div style={{ fontSize:'13px', color:'var(--text-primary)', lineHeight:1.6 }}>{product.additional_details}</div></div>}
              {product.notes && <div><div style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', marginBottom:'3px' }}>Notes</div><div style={{ fontSize:'13px', color:'var(--text-primary)', lineHeight:1.6 }}>{product.notes}</div></div>}
            </div>
          </SectionCard>
        )}

        {/* ── Stock history ── */}
        {stockLog.length>0 && (
          <SectionCard title="Stock history 🔒" color="var(--warning)" icon="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z">
            <div style={{ display:'flex', flexDirection:'column' }}>
              {stockLog.map((log,i)=>{
                const isRestock=log.action==='restock'; const qty=Math.abs(log.quantity); const sign=log.quantity>0?'+':'-'
                const color=isRestock?'var(--success)':'#DC2626'; const bg=isRestock?'#F0FDF4':'#FEF2F2'
                return (
                  <div key={log.id} style={{ display:'flex', alignItems:'flex-start', gap:'10px', padding:'10px 0', borderBottom:i<stockLog.length-1?'1px solid var(--bg-muted)':'none' }}>
                    <div style={{ width:'28px', height:'28px', borderRadius:'50%', background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <span style={{ fontSize:'12px', color, fontWeight:700 }}>{sign}</span>
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>
                        {isRestock?'Restocked':log.action==='adjustment'?'Adjusted':'Deducted'}{' '}
                        <span style={{ color }}>{sign}{qty}</span>
                      </div>
                      <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>
                        {new Date(log.created_at).toLocaleString('en-BD',{day:'numeric',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'})} · {log.done_by}
                        {log.buy_price && <span> · {fmt(log.buy_price)}/pc</span>}
                      </div>
                      {log.note && <div style={{ fontSize:'12px', color:'var(--text-muted)', marginTop:'2px', fontStyle:'italic' }}>{log.note}</div>}
                    </div>
                  </div>
                )
              })}
            </div>
          </SectionCard>
        )}
      </div>

      {/* ── Delete confirm ── */}
      {showDeleteConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:150, padding:'16px' }}>
          <div style={{ background:'var(--bg-card)', borderRadius:'16px', padding:'24px', maxWidth:'360px', width:'100%' }}>
            <div style={{ textAlign:'center', marginBottom:'12px' }}><Icon path={IC.trash} color="#DC2626" size={36} strokeWidth={1.5}/></div>
            <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', textAlign:'center', marginBottom:'6px' }}>Delete this product?</div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)', textAlign:'center', marginBottom:'16px' }}>Deletes all stock history and variants. Cannot be undone.</div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>setShowDeleteConfirm(false)} style={{ flex:1, padding:'11px', borderRadius:'10px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Cancel</button>
              <button onClick={handleDeleteProduct} disabled={deleting} style={{ flex:1, padding:'11px', borderRadius:'10px', background:'#DC2626', border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer', opacity:deleting?0.6:1 }}>
                {deleting?'Deleting...':'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Restock sheet ── */}
      {showRestock && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50 }}>
          <div style={{ background:'var(--bg-card)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:'520px', padding:'20px', fontFamily:'Space Grotesk' }}>
            <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px' }}>
              Restock — {restockVariantId?(variants.find(v=>v.id===restockVariantId)?.variant_name+' variant'):product.name}
            </div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'14px' }}>
              Current: <strong style={{ color:stockColor(restockVariantId?(variants.find(v=>v.id===restockVariantId)?.stock||0):product.stock) }}>
                {restockVariantId?variants.find(v=>v.id===restockVariantId)?.stock:product.stock}
              </strong>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'14px' }}>
              {[
                { label:'Current',   value:restockVariantId?(variants.find(v=>v.id===restockVariantId)?.stock||0):product.stock, bg:'#FEF2F2', color:'#DC2626' },
                { label:'Adding',    value:'+' +(restockQty||0),  bg:'var(--primary-light)', color:'var(--primary)' },
                { label:'New total', value:(restockVariantId?(variants.find(v=>v.id===restockVariantId)?.stock||0):product.stock)+Number(restockQty||0), bg:'#F0FDF4', color:'var(--success)' },
              ].map(s=>(
                <div key={s.label} style={{ background:s.bg, borderRadius:'10px', padding:'10px', textAlign:'center' }}>
                  <div style={{ fontSize:'20px', fontWeight:700, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Quantity received *</label>
                <input type="number" value={restockQty} onChange={e=>setRestockQty(e.target.value)} placeholder="0" style={{ ...inp, fontSize:'14px' }} autoFocus/>
              </div>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Buy price this batch 🔒 <span style={{ fontWeight:400 }}>optional</span></label>
                <input type="number" value={restockPrice} onChange={e=>setRestockPrice(e.target.value)} placeholder="0" style={inp}/>
              </div>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Note <span style={{ fontWeight:400 }}>optional</span></label>
                <input type="text" value={restockNote} onChange={e=>setRestockNote(e.target.value)} placeholder="e.g. Bought from Mirpur market" style={inp}/>
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>{ setShowRestock(false); setRestockVariantId(null) }} style={{ flex:1, padding:'12px', borderRadius:'10px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Cancel</button>
              <button onClick={handleRestock} disabled={saving||!restockQty} style={{ flex:2, padding:'12px', borderRadius:'10px', background:'var(--success)', border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer', opacity:(saving||!restockQty)?0.6:1 }}>
                {saving?'Saving...':'Confirm restock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Adjust sheet ── */}
      {showAdjust && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'flex-end', justifyContent:'center', zIndex:50 }}>
          <div style={{ background:'var(--bg-card)', borderRadius:'20px 20px 0 0', width:'100%', maxWidth:'520px', padding:'20px', fontFamily:'Space Grotesk' }}>
            <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px' }}>Adjust stock — {product.name}</div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'14px' }}>Current: <strong style={{ color:stockColor(product.stock) }}>{product.stock}</strong></div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'14px' }}>
              {[
                { label:'Current',   value:product.stock, bg:'#FEF2F2', color:'#DC2626' },
                { label:'Removing',  value:'-'+(adjustQty||0), bg:'#FFF8E6', color:'#D97706' },
                { label:'New total', value:Math.max(0,(product.stock||0)-Number(adjustQty||0)), bg:'#F0FDF4', color:'var(--success)' },
              ].map(s=>(
                <div key={s.label} style={{ background:s.bg, borderRadius:'10px', padding:'10px', textAlign:'center' }}>
                  <div style={{ fontSize:'20px', fontWeight:700, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'2px' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:'10px', marginBottom:'16px' }}>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Quantity to remove *</label>
                <input type="number" value={adjustQty} onChange={e=>setAdjustQty(e.target.value)} placeholder="0" style={{ ...inp, fontSize:'14px' }} autoFocus/>
              </div>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Reason *</label>
                <select value={adjustReason} onChange={e=>setAdjustReason(e.target.value)} style={inp}>
                  {ADJUST_REASONS.map(r=><option key={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Note <span style={{ fontWeight:400 }}>optional</span></label>
                <input type="text" value={adjustNote} onChange={e=>setAdjustNote(e.target.value)} placeholder="e.g. 2 items broke during dispatch" style={inp}/>
              </div>
            </div>
            <div style={{ display:'flex', gap:'10px' }}>
              <button onClick={()=>setShowAdjust(false)} style={{ flex:1, padding:'12px', borderRadius:'10px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer' }}>Cancel</button>
              <button onClick={handleAdjust} disabled={saving||!adjustQty||Number(adjustQty)<=0} style={{ flex:2, padding:'12px', borderRadius:'10px', background:'#D97706', border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer', opacity:(saving||!adjustQty||Number(adjustQty)<=0)?0.6:1 }}>
                {saving?'Saving...':'Confirm adjustment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {cropSrc && <CropModal src={cropSrc} onSave={handleCropSave} onCancel={()=>setCropSrc(null)} shopName={seller?.shop_name||''} productCode={product?.product_code||''} logoUrl={sellerLogo}/>}
      {showBarcodeModal && product && <BarcodeLabelModal product={product} variants={variants} shopName={seller?.shop_name||'PageConnect'} onClose={()=>setShowBarcodeModal(false)}/>}
      {showCardModal && product && <ProductCardModal product={product} shopName={seller?.shop_name||'PageConnect'} onClose={()=>setShowCardModal(false)}/>}
      {showFbPost && product && <FbPostModal product={product} seller={seller} onClose={()=>setShowFbPost(false)} onSuccess={()=>setShowFbPost(false)}/>}
      {showAddPostLink && <AddPostLinkModal productId={id} sellerId={seller?.id} onSave={()=>{ setShowAddPostLink(false); fetchProduct() }} onClose={()=>setShowAddPostLink(false)}/>}

      {postPhotoLightbox && postPhotoPreview && (
        <div onClick={()=>setPostPhotoLightbox(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'20px' }}>
          <button onClick={()=>setPostPhotoLightbox(false)} style={{ position:'absolute', top:'16px', right:'16px', width:'38px', height:'38px', borderRadius:'50%', background:'rgba(255,255,255,0.15)', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
          <img src={postPhotoPreview} alt={product.name} onClick={e=>e.stopPropagation()} style={{ maxWidth:'90vw', maxHeight:'80vh', borderRadius:'12px', objectFit:'contain' }}/>
          <div style={{ position:'absolute', bottom:'20px' }} onClick={e=>e.stopPropagation()}>
            <a href={postPhotoPreview} download={product.product_code+'-post.jpg'} style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 18px', borderRadius:'10px', background:'var(--primary)', color:'#fff', fontSize:'13px', fontWeight:600, textDecoration:'none', fontFamily:'Space Grotesk, sans-serif' }}>
              Download
            </a>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pd-spin { to { transform:rotate(360deg) } }
        @media(max-width:640px) {
          .pd-stats { grid-template-columns:1fr 1fr !important; }
          .pd-edit-inline { display:none !important; }
          .pd-edit-sheet { display:block !important; }
          .pd-header-layout { flex-direction:column-reverse !important; }
          .pd-post-photo-col { width:100% !important; }
        }
        @media(min-width:641px) { .pd-edit-sheet { display:none !important; } }
      `}</style>
    </div>
  )
}

function EditFormContent({ editForm, setEF, imagePreview, setImagePreview, setImageFile, imgInputRef, handleImageSelect, postPhotoPreview, setPostPhotoPreview, setPostPhotoFile, postImgInputRef, handlePostImageSelect, savingEdit, uploadingImg, handleSaveEdit, setShowDeleteConfirm, sym, IC, inp }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>

      {/* Two photo slots */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'8px' }}>Thumbnail photo</label>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
            {imagePreview
              ? <div style={{ position:'relative' }}>
                  <img src={imagePreview} alt="thumb" style={{ width:'80px', height:'80px', borderRadius:'10px', objectFit:'cover', border:'2px solid var(--primary)' }}/>
                  <button onClick={()=>{ setImagePreview(null); setImageFile(null) }} style={{ position:'absolute', top:'-6px', right:'-6px', width:'18px', height:'18px', borderRadius:'50%', background:'#DC2626', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              : <div style={{ width:'80px', height:'80px', borderRadius:'10px', border:'2px dashed var(--border)', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                </div>
            }
            <button onClick={()=>imgInputRef.current?.click()} style={{ width:'100%', padding:'6px 10px', borderRadius:'8px', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'11.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer' }}>
              {imagePreview?'Change':'Upload'}
            </button>
            <div style={{ fontSize:'10.5px', color:'var(--text-muted)', textAlign:'center' }}>For inventory</div>
          </div>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'#1877F2', display:'block', marginBottom:'8px' }}>Post photo</label>
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'8px' }}>
            {postPhotoPreview
              ? <div style={{ position:'relative' }}>
                  <img src={postPhotoPreview} alt="post" style={{ width:'80px', height:'80px', borderRadius:'10px', objectFit:'cover', border:'2px solid #1877F2' }}/>
                  <button onClick={()=>{ setPostPhotoPreview(null); setPostPhotoFile(null) }} style={{ position:'absolute', top:'-6px', right:'-6px', width:'18px', height:'18px', borderRadius:'50%', background:'#DC2626', border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round"><path d="M6 18L18 6M6 6l12 12"/></svg>
                  </button>
                </div>
              : <div style={{ width:'80px', height:'80px', borderRadius:'10px', border:'2px dashed #1877F2', background:'var(--bg-muted)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }} onClick={()=>postImgInputRef.current?.click()}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1877F2" strokeWidth="1.5" strokeLinecap="round"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z M12 17a4 4 0 100-8 4 4 0 000 8z"/></svg>
                </div>
            }
            <button onClick={()=>postImgInputRef.current?.click()} style={{ width:'100%', padding:'6px 10px', borderRadius:'8px', border:'1px solid #1877F2', background:'#EFF6FF', fontSize:'11.5px', fontWeight:600, color:'#1877F2', cursor:'pointer' }}>
              {postPhotoPreview?'Change':'Upload'}
            </button>
            <div style={{ fontSize:'10.5px', color:'var(--text-muted)', textAlign:'center' }}>For Facebook posts</div>
          </div>
        </div>
      </div>

      {/* Product fields */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Product type</label>
          <input value={editForm.product_type} onChange={e=>setEF('product_type',e.target.value)} style={inp}/>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Category</label>
          <select value={editForm.category} onChange={e=>setEF('category',e.target.value)} style={inp}>
            {CATEGORIES.map(c=><option key={c}>{c}</option>)}
          </select>
        </div>
        <div style={{ gridColumn:'span 2' }}>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Product name *</label>
          <input value={editForm.name} onChange={e=>setEF('name',e.target.value)} style={inp}/>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Color / Visual variation</label>
          <input value={editForm.color} onChange={e=>setEF('color',e.target.value)} placeholder="e.g. Red, Navy Blue" style={inp}/>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Season</label>
          <select value={editForm.season} onChange={e=>setEF('season',e.target.value)} style={inp}>
            <option value="all_year">All year</option>
            <option value="eid">Eid</option>
            <option value="winter">Winter</option>
            <option value="wedding">Wedding</option>
            <option value="puja">Puja</option>
            <option value="summer">Summer</option>
            <option value="fair">Fair / Event</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Sell price</label>
          <input type="number" value={editForm.sell_price} onChange={e=>setEF('sell_price',e.target.value)} style={inp} placeholder="0"/>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Buy price 🔒</label>
          <input type="number" value={editForm.buy_price} onChange={e=>setEF('buy_price',e.target.value)} style={inp} placeholder="0"/>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>VAT / GST % <span style={{ fontWeight:400 }}>optional</span></label>
          <input type="number" value={editForm.vat_percent} onChange={e=>setEF('vat_percent',e.target.value)} style={inp} placeholder="e.g. 15"/>
        </div>
        <div>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Status</label>
          <select value={editForm.status} onChange={e=>setEF('status',e.target.value)} style={inp}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <div style={{ gridColumn:'span 2' }}>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Additional details</label>
          <textarea value={editForm.additional_details} onChange={e=>setEF('additional_details',e.target.value)} rows={2} style={{ ...inp, resize:'none' }}/>
        </div>
        <div style={{ gridColumn:'span 2' }}>
          <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Internal notes</label>
          <textarea value={editForm.notes} onChange={e=>setEF('notes',e.target.value)} rows={2} style={{ ...inp, resize:'none' }}/>
        </div>
        <div style={{ gridColumn:'span 2', display:'flex', alignItems:'center', gap:'10px' }}>
          <input type="checkbox" checked={editForm.is_pinned} onChange={e=>setEF('is_pinned',e.target.checked)} style={{ width:'16px', height:'16px', accentColor:'var(--primary)' }}/>
          <label style={{ fontSize:'12.5px', color:'var(--text-primary)' }}>Pin as bestseller ⭐</label>
        </div>
      </div>

      {/* Offer section */}
      <div style={{ borderTop:'1px solid var(--border)', paddingTop:'14px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:editForm.offer_active?'14px':'0' }}>
          <div>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)' }}>Offer / Discount</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>Shown in inventory and Facebook captions</div>
          </div>
          <button onClick={()=>setEF('offer_active',!editForm.offer_active)} style={{ width:'40px', height:'22px', borderRadius:'11px', background:editForm.offer_active?'var(--success)':'var(--border)', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
            <div style={{ position:'absolute', top:'3px', left:editForm.offer_active?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
          </button>
        </div>
        {editForm.offer_active && (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Offer type</label>
              <select value={editForm.offer_type} onChange={e=>setEF('offer_type',e.target.value)} style={inp}>
                {OFFER_TYPES.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            {['percent','fixed'].includes(editForm.offer_type) && (
              <div>
                <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>{editForm.offer_type==='percent'?'Discount %':'Amount off'}</label>
                <input type="number" value={editForm.offer_value} onChange={e=>setEF('offer_value',e.target.value)} style={inp} placeholder={editForm.offer_type==='percent'?'e.g. 20':'e.g. 200'}/>
              </div>
            )}
            <div style={{ gridColumn:'span 2' }}>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Offer label <span style={{ fontWeight:400 }}>optional</span></label>
              <input value={editForm.offer_label} onChange={e=>setEF('offer_label',e.target.value)} style={inp} placeholder="e.g. Eid Special, Clearance"/>
            </div>
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Start date</label>
              <input type="date" value={editForm.offer_valid_from} onChange={e=>setEF('offer_valid_from',e.target.value)} style={inp}/>
            </div>
            <div>
              <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>End date <span style={{ fontWeight:400 }}>optional</span></label>
              <input type="date" value={editForm.offer_valid_until} onChange={e=>setEF('offer_valid_until',e.target.value)} style={inp}/>
            </div>
          </div>
        )}
      </div>

      {/* ── NEW: Facebook post status ── */}
      <div style={{ borderTop:'1px solid var(--border)', paddingTop:'14px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:editForm.posted_on_facebook?'12px':'0' }}>
          <div>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)' }}>Posted on Facebook</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>Toggle on once shared on your page</div>
          </div>
          <button onClick={()=>setEF('posted_on_facebook',!editForm.posted_on_facebook)} style={{ width:'40px', height:'22px', borderRadius:'11px', background:editForm.posted_on_facebook?'#1877F2':'var(--border)', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
            <div style={{ position:'absolute', top:'3px', left:editForm.posted_on_facebook?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
          </button>
        </div>
        {editForm.posted_on_facebook && (
          <div style={{ marginBottom:'4px' }}>
            <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Date posted <span style={{ fontWeight:400 }}>optional</span></label>
            <input type="date" value={editForm.fb_posted_date} onChange={e=>setEF('fb_posted_date',e.target.value)} style={inp}/>
          </div>
        )}
      </div>

      {/* ── NEW: Variable pricing ── */}
      <div style={{ borderTop:'1px solid var(--border)', paddingTop:'14px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:editForm.variable_pricing?'12px':'0' }}>
          <div>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)' }}>Variable pricing (Tara bargaining)</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>Tara can negotiate down to your floor price</div>
          </div>
          <button onClick={()=>setEF('variable_pricing',!editForm.variable_pricing)} style={{ width:'40px', height:'22px', borderRadius:'11px', background:editForm.variable_pricing?'#7C3AED':'var(--border)', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
            <div style={{ position:'absolute', top:'3px', left:editForm.variable_pricing?'21px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s' }}/>
          </button>
        </div>
        {editForm.variable_pricing && (
          <div style={{ marginBottom:'4px' }}>
            <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Floor price — lowest Tara can agree to</label>
            <input type="number" value={editForm.best_price} onChange={e=>setEF('best_price',e.target.value)} placeholder="e.g. 850" style={inp}/>
          </div>
        )}
      </div>

      {/* ── NEW: Advance payment ── */}
      <div style={{ borderTop:'1px solid var(--border)', paddingTop:'14px' }}>
        <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px' }}>Advance payment requirement</div>
        <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'10px' }}>Tara and the order form read this automatically</div>
        <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'10px' }}>
          {[
            { v:'none',    l:'No advance',             sub:'Customer pays on delivery' },
            { v:'percent', l:'Percentage advance',     sub:'e.g. 30% upfront' },
            { v:'fixed',   l:'Fixed amount advance',   sub:'e.g. flat ' + sym + '200 upfront' },
            { v:'full',    l:'Full payment required',  sub:'100% advance — no COD' },
          ].map(opt=>(
            <button key={opt.v} onClick={()=>setEF('advance_payment_type',opt.v)}
              style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 12px', borderRadius:'9px', border:'1.5px solid', borderColor:editForm.advance_payment_type===opt.v?'#0D9488':'var(--border)', background:editForm.advance_payment_type===opt.v?'rgba(13,148,136,0.07)':'var(--bg-card)', cursor:'pointer', textAlign:'left' }}>
              <div style={{ width:'14px', height:'14px', borderRadius:'50%', border:'2px solid', borderColor:editForm.advance_payment_type===opt.v?'#0D9488':'var(--border)', background:editForm.advance_payment_type===opt.v?'#0D9488':'transparent', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {editForm.advance_payment_type===opt.v && <div style={{ width:'5px', height:'5px', borderRadius:'50%', background:'#fff' }}/>}
              </div>
              <div>
                <div style={{ fontSize:'12.5px', fontWeight:600, color:editForm.advance_payment_type===opt.v?'#0D9488':'var(--text-primary)' }}>{opt.l}</div>
                <div style={{ fontSize:'11px', color:'var(--text-muted)', marginTop:'1px' }}>{opt.sub}</div>
              </div>
            </button>
          ))}
        </div>
        {editForm.advance_payment_type==='percent' && (
          <div style={{ marginBottom:'4px' }}>
            <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Advance %</label>
            <input type="number" value={editForm.advance_payment_value} onChange={e=>setEF('advance_payment_value',e.target.value)} placeholder="e.g. 30" style={inp}/>
          </div>
        )}
        {editForm.advance_payment_type==='fixed' && (
          <div style={{ marginBottom:'4px' }}>
            <label style={{ fontSize:'11.5px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'5px' }}>Fixed advance amount ({sym})</label>
            <input type="number" value={editForm.advance_payment_value} onChange={e=>setEF('advance_payment_value',e.target.value)} placeholder="e.g. 200" style={inp}/>
          </div>
        )}
        {editForm.advance_payment_type==='full' && (
          <div style={{ padding:'8px 12px', borderRadius:'9px', background:'rgba(13,148,136,0.07)', border:'1px solid rgba(13,148,136,0.25)', fontSize:'11.5px', color:'#0D9488' }}>
            Customer must pay in full before the order is confirmed. No COD.
          </div>
        )}
      </div>

      {/* Delete + Save buttons */}
      <div style={{ display:'flex', gap:'8px', paddingTop:'4px' }}>
        <button onClick={()=>setShowDeleteConfirm(true)} style={{ flex:1, padding:'11px', borderRadius:'9px', background:'#FEF2F2', border:'1px solid #FECACA', fontSize:'12.5px', fontWeight:600, color:'#DC2626', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
          Delete
        </button>
        <button onClick={handleSaveEdit} disabled={savingEdit||uploadingImg} style={{ flex:2, padding:'11px', borderRadius:'9px', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:700, color:'#fff', cursor:'pointer', opacity:(savingEdit||uploadingImg)?0.6:1 }}>
          {savingEdit?'Saving...':uploadingImg?'Uploading...':'Save changes'}
        </button>
      </div>
    </div>
  )
}

export default ProductDetail